import React from 'react';
import { HIGHLIGHTERS } from '../../lib/paper';

export type DoodleKind = 'page' | 'chat' | 'notebooks' | 'search' | 'coffee' | 'sun' | 'lamp' | 'moon';

const INK = 'var(--on-surface)';
const PAPER = 'var(--surface-container-lowest)';
const H = HIGHLIGHTERS;

/** Common stroke props for the ink lines. */
const ink = {
  stroke: INK,
  strokeWidth: 2.2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/** Four-point sparkle centered on (x, y). */
function Sparkle({ x, y, r = 5 }: { x: number; y: number; r?: number }): React.ReactElement {
  return <path d={`M${x} ${y - r} L${x} ${y + r} M${x - r} ${y} L${x + r} ${y}`} {...ink} strokeWidth={1.8} />;
}

function PageArt(): React.ReactElement {
  return (
    <g>
      <path d="M28 72 C 26 30, 148 22, 156 64 C 164 106, 36 118, 28 72 Z" fill={H.yellow.fill} />
      <path d="M55 20 L130 27 L123 112 L47 105 Z" fill={PAPER} {...ink} />
      <path d="M68 23 L61 107" stroke={H.coral.ink} strokeWidth={1.4} strokeLinecap="round" opacity={0.6} />
      <path d="M72 46 L118 50 M71 60 L117 64 M70 74 L106 77 M69 88 L98 90" {...ink} strokeWidth={1.6} opacity={0.35} />
      <path d="M104 118 L150 80 L158 90 L112 128 Z" fill={H.yellow.fill} {...ink} />
      <path d="M104 118 L112 128 L98 131 Z" fill={H.coral.fill} {...ink} />
      <path d="M150 80 L156 75 L164 85 L158 90 Z" fill={H.pink.fill} {...ink} />
      <Sparkle x={34} y={30} />
      <Sparkle x={150} y={36} r={4} />
      <circle cx={160} cy={58} r={2} fill={INK} />
    </g>
  );
}

function ChatArt(): React.ReactElement {
  return (
    <g>
      <path d="M22 70 C 22 28, 150 20, 158 62 C 166 104, 30 116, 22 70 Z" fill={H.lilac.fill} />
      <path
        d="M44 28 H100 Q112 28 112 40 V64 Q112 76 100 76 H66 L50 90 L53 76 H44 Q32 76 32 64 V40 Q32 28 44 28 Z"
        fill={PAPER}
        {...ink}
      />
      <circle cx={56} cy={52} r={3.2} fill={INK} />
      <circle cx={72} cy={52} r={3.2} fill={INK} />
      <circle cx={88} cy={52} r={3.2} fill={INK} />
      <path
        d="M98 70 H140 Q150 70 150 80 V96 Q150 106 140 106 H136 L138 116 L126 106 H98 Q88 106 88 96 V80 Q88 70 98 70 Z"
        fill={H.mint.fill}
        {...ink}
      />
      <path d="M100 84 H136 M100 94 H124" {...ink} strokeWidth={1.8} />
      <path d="M146 22 C 136 22 132 32 138 39 L138 45 L150 45 L150 39 C 156 32 154 22 146 22 Z" fill={H.yellow.fill} {...ink} />
      <path d="M140 50 H148" {...ink} />
      <path d="M130 20 L126 16 M162 20 L166 16 M146 12 L146 7" {...ink} strokeWidth={1.8} />
    </g>
  );
}

function NotebooksArt(): React.ReactElement {
  return (
    <g>
      <path d="M24 76 C 20 34, 150 26, 158 68 C 166 110, 30 120, 24 76 Z" fill={H.mint.fill} />
      <path d="M40 96 L136 90 L138 112 L42 118 Z" fill={H.coral.fill} {...ink} />
      <path d="M52 95 L54 117" {...ink} strokeWidth={1.6} />
      <path d="M48 72 L140 76 L139 92 L47 88 Z" fill={H.sky.fill} {...ink} />
      <path d="M126 75 L125 91" {...ink} strokeWidth={1.6} />
      <path d="M44 48 L132 42 L134 68 L46 74 Z" fill={H.yellow.fill} {...ink} />
      <path d="M60 55 L110 52" {...ink} strokeWidth={1.6} opacity={0.5} />
      <path d="M114 41 L114 22 L122 28 L130 22 L130 42" fill={H.pink.fill} {...ink} />
      <Sparkle x={30} y={36} />
      <Sparkle x={152} y={34} r={4} />
    </g>
  );
}

function SearchArt(): React.ReactElement {
  return (
    <g>
      <path d="M26 70 C 24 30, 150 22, 156 64 C 162 106, 32 116, 26 70 Z" fill={H.sky.fill} />
      <circle cx={80} cy={60} r={30} fill={PAPER} {...ink} />
      <path d="M62 50 Q70 38 84 40" {...ink} strokeWidth={1.8} opacity={0.4} />
      <path d="M102 82 L128 108" {...ink} strokeWidth={7} />
      <path d="M102 82 L128 108" stroke={H.yellow.fill} strokeWidth={3} strokeLinecap="round" />
      <path d="M140 34 Q146 24 138 20 Q130 18 130 26 M136 42 L136 43" {...ink} strokeWidth={1.8} />
      <Sparkle x={36} y={34} />
    </g>
  );
}

/*
 * Time-of-day scenes for the Home masthead corner. They render at about half
 * size, so they ink with a heavier pen to land at the same visual weight.
 */
const bold = { ...ink, strokeWidth: 4 };

function CoffeeArt(): React.ReactElement {
  return (
    <g>
      <path d="M36 82 C 32 44, 150 38, 154 78 C 158 116, 42 124, 36 82 Z" fill={H.yellow.fill} />
      <path d="M74 50 Q66 40 74 30 Q82 20 76 10 M98 50 Q90 40 98 30 Q106 20 100 10" {...bold} strokeWidth={3} opacity={0.55} />
      <path d="M58 58 H116 V100 Q116 116 100 116 H74 Q58 116 58 100 Z" fill={PAPER} {...bold} />
      <path d="M61 70 H113 V82 H61 Z" fill={H.coral.fill} />
      <path d="M116 68 Q138 68 138 86 Q138 102 116 100" fill="none" {...bold} />
      <Sparkle x={148} y={30} r={7} />
      <circle cx={30} cy={40} r={3} fill={INK} />
    </g>
  );
}

function SunArt(): React.ReactElement {
  const rays = [0, 45, 90, 135, 180, 225, 270, 315].map((deg) => {
    const a = (deg * Math.PI) / 180;
    return `M${90 + Math.cos(a) * 40} ${67 + Math.sin(a) * 40} L${90 + Math.cos(a) * 54} ${67 + Math.sin(a) * 54}`;
  });
  return (
    <g>
      <path d="M30 78 C 26 40, 150 32, 156 72 C 162 112, 36 120, 30 78 Z" fill={H.coral.fill} />
      <path d={rays.join(' ')} {...bold} />
      <circle cx={90} cy={67} r={28} fill={H.yellow.fill} {...bold} />
      <circle cx={80} cy={62} r={3.4} fill={INK} />
      <circle cx={100} cy={62} r={3.4} fill={INK} />
      <path d="M78 74 Q90 84 102 74" fill="none" {...bold} strokeWidth={3.4} />
    </g>
  );
}

function LampArt(): React.ReactElement {
  return (
    <g>
      <path d="M28 80 C 24 42, 148 34, 154 74 C 160 114, 34 122, 28 80 Z" fill={H.lilac.fill} />
      <path d="M70 62 L44 116 H122 L106 62 Z" fill={H.yellow.fill} opacity={0.9} />
      <path d="M100 36 L124 18 L140 50 L140 112" fill="none" {...bold} />
      <path d="M126 116 H154" {...bold} strokeWidth={6} />
      <path d="M66 62 L110 62 L100 34 L78 34 Z" fill={H.coral.fill} {...bold} />
      <path d="M30 118 H120" {...bold} strokeWidth={3} opacity={0.35} />
      <Sparkle x={38} y={34} r={6} />
    </g>
  );
}

function MoonArt(): React.ReactElement {
  return (
    <g>
      <path d="M28 76 C 24 38, 150 30, 156 70 C 162 110, 34 118, 28 76 Z" fill={H.sky.fill} />
      {/* Crescent: the outer circle's long arc, back along the inner circle's bite. */}
      <path d="M88 32 A36 36 0 1 0 121.2 81.8 A30 30 0 0 1 88 32 Z" fill={H.yellow.fill} {...bold} />
      <path d="M130 84 H144 L130 100 H144 M150 60 H158 L150 70 H158" fill="none" {...bold} strokeWidth={3} />
      <Sparkle x={140} y={30} r={7} />
      <Sparkle x={36} y={30} r={5} />
      <circle cx={128} cy={50} r={2.6} fill={INK} />
    </g>
  );
}

const ART: Record<DoodleKind, () => React.ReactElement> = {
  page: PageArt,
  chat: ChatArt,
  notebooks: NotebooksArt,
  search: SearchArt,
  coffee: CoffeeArt,
  sun: SunArt,
  lamp: LampArt,
  moon: MoonArt,
};

/**
 * Hand-inked illustration for empty states and the Home masthead — the same
 * art as mobile's Doodle (mobile/src/components/fun/doodle.tsx). Bobs gently
 * so an empty screen still feels alive. 180×134 at `size = 1`.
 */
export const Doodle: React.FC<{ kind: DoodleKind; size?: number; className?: string }> = ({
  kind,
  size = 1,
  className = '',
}) => {
  const Art = ART[kind];
  return (
    <svg
      aria-hidden
      width={180 * size}
      height={134 * size}
      viewBox="0 0 180 134"
      className={`pb-bob pointer-events-none shrink-0 ${className}`}
    >
      <Art />
    </svg>
  );
};
